class AudioService {
  constructor() {
    this.audioContext = null;
    this.audioQueue = [];
    this.isPlaying = false;
    this.isInitialized = false;
    this.silenceSource = null;
    this.silenceGain = null;
    this.keepAudioAlive = false;
    this.currentAudio = null; // Track currently playing audio element
    this.pendingAudioElements = []; // Track all audio elements for cleanup
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      // Initialize AudioContext - requires user gesture
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      console.log('AudioContext created, state:', this.audioContext.state);
      
      // Resume AudioContext if suspended (iOS requirement)
      if (this.audioContext.state === 'suspended') {
        console.log('AudioContext suspended, resuming...');
        await this.audioContext.resume();
        console.log('AudioContext resumed, new state:', this.audioContext.state);
      }

      this.isInitialized = true;
      console.log('Audio service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize audio service:', error);
      throw error;
    }
  }

  async playAudioChunk(audioData) {
    if (!this.isInitialized) {
      console.warn('Audio service not initialized');
      return;
    }

    console.log('AudioService.playAudioChunk called with:', {
      dataType: typeof audioData,
      isUint8Array: audioData instanceof Uint8Array,
      isArray: Array.isArray(audioData),
      length: audioData?.length || 0
    });

    // Check if audio data is empty
    if (!audioData || audioData.length === 0) {
      console.warn('Received empty audio data, skipping...');
      return;
    }

    try {
      // Ensure audio context is running (user interaction required)
      if (this.audioContext.state === 'suspended') {
        console.log('AudioContext suspended, attempting to resume...');
        try {
          await this.audioContext.resume();
          console.log('AudioContext resumed successfully');
        } catch (resumeError) {
          console.error('Failed to resume AudioContext:', resumeError);
          console.log('User interaction may be required to enable audio');
          throw new Error('The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.');
        }
      }

      // Add audio data to queue
      this.audioQueue.push(audioData);
      
      // Start processing queue if not already playing
      if (!this.isPlaying) {
        await this.processAudioQueue();
      }
    } catch (error) {
      console.error('Error playing audio chunk:', error);
      throw error; // Propagate error to caller
    }
  }

  async processAudioQueue() {
    if (this.isPlaying || this.audioQueue.length === 0) {
      return;
    }

    this.isPlaying = true;

    try {
      while (this.audioQueue.length > 0) {
        const audioData = this.audioQueue.shift();

        // CRITICAL: Stop any currently playing audio before starting new one
        // This prevents overlapping audio which causes crackling
        if (this.currentAudio) {
          console.log('Stopping previous audio before playing next segment');
          try {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            this.currentAudio = null;
          } catch (e) {
            console.warn('Error stopping previous audio:', e);
          }
        }

        // Wait for current audio to finish completely before starting next
        await this.playAudioBuffer(audioData);

        // NO DELAY - let audio play naturally, await ensures completion
      }
    } catch (error) {
      console.error('Error processing audio queue:', error);
      // Propagate permission errors
      if (error.message && error.message.includes('not allowed')) {
        throw error;
      }
    } finally {
      this.isPlaying = false;
      this.currentAudio = null;
    }
  }

  async playAudioBuffer(audioData) {
    return new Promise((resolve, reject) => {
      try {
        console.log('playAudioBuffer called with audio data length:', audioData.length);
        
        // Log first bytes for debugging
        if (audioData.length >= 16) {
          const headerBytes = Array.from(audioData.slice(0, 16));
          const headerHex = headerBytes.map(b => b.toString(16).padStart(2, '0')).join(' ');
          const headerText = String.fromCharCode(...headerBytes.slice(0, 8));
          console.log('Audio header (hex):', headerHex);
          console.log('Audio header (text):', headerText);
        } else if (audioData.length > 0) {
          const allBytes = Array.from(audioData);
          const allHex = allBytes.map(b => b.toString(16).padStart(2, '0')).join(' ');
          console.log('All audio bytes (hex):', allHex);
        }

        // Since OpenAI TTS returns MP3 by default, try MP3 first
        console.log('Attempting MP3 playback (OpenAI TTS format)...');
        this.playWithAudioElement(audioData, 'mp3').then(resolve).catch((mp3Error) => {
          console.log('MP3 playback failed:', mp3Error.message);
          
          // Check if this is a permission error
          if (mp3Error.message && mp3Error.message.includes('not allowed')) {
            console.log('Audio permission required - user interaction needed');
            reject(mp3Error); // Propagate permission error
            return;
          }
          
          console.log('Trying WAV format...');
          this.playWithAudioElement(audioData, 'wav').then(resolve).catch((wavError) => {
            console.log('WAV playback failed:', wavError.message);
            console.log('Trying raw PCM conversion...');
            
            this.tryRawAudioPlayback(audioData).then(resolve).catch((rawError) => {
              console.error('All audio playback methods failed:', {
                mp3Error: mp3Error.message,
                wavError: wavError.message,
                rawError: rawError.message
              });
              resolve(); // Don't throw, just log and continue
            });
          });
        });
        
      } catch (error) {
        console.error('Error in playAudioBuffer:', error);
        resolve();
      }
    });
  }

  detectAudioFormat(audioData) {
    if (audioData.length < 4) return 'unknown';

    // Check for WAV (RIFF header)
    if (audioData[0] === 0x52 && audioData[1] === 0x49 && 
        audioData[2] === 0x46 && audioData[3] === 0x46) {
      return 'wav';
    }

    // Check for MP3 (ID3 header)
    if (audioData.length >= 3) {
      // ID3v2 header (most common for OpenAI TTS)
      if (audioData[0] === 0x49 && audioData[1] === 0x44 && audioData[2] === 0x33) {
        return 'mp3';
      }
    }

    // Check for MP3 frame sync (FF Fx) - can appear at beginning or after ID3
    for (let i = 0; i < Math.min(1024, audioData.length - 1); i++) {
      if (audioData[i] === 0xFF && (audioData[i + 1] & 0xE0) === 0xE0) {
        return 'mp3';
      }
    }

    // Check for other common audio formats
    // AAC (ADTS)
    if (audioData.length >= 2 && audioData[0] === 0xFF && (audioData[1] & 0xF0) === 0xF0) {
      return 'aac';
    }

    // FLAC
    if (audioData.length >= 4 && audioData[0] === 0x66 && audioData[1] === 0x4C && 
        audioData[2] === 0x61 && audioData[3] === 0x43) {
      return 'flac';
    }

    // OGG/Opus
    if (audioData.length >= 4 && audioData[0] === 0x4F && audioData[1] === 0x67 && 
        audioData[2] === 0x67 && audioData[3] === 0x53) {
      return 'ogg';
    }

    // If no specific format detected, likely raw PCM or unknown
    return 'pcm';
  }

  async playWithAudioElement(audioData, format) {
    return new Promise((resolve, reject) => {
      try {
        // Map format to MIME type
        const mimeType = {
          'wav': 'audio/wav',
          'mp3': 'audio/mpeg',
          'aac': 'audio/aac',
          'flac': 'audio/flac',
          'ogg': 'audio/ogg'
        }[format] || 'audio/mpeg'; // Default to MP3 for OpenAI TTS

        console.log(`Playing audio as ${format} with MIME type: ${mimeType}, data size: ${audioData.length}`);
        const blob = new Blob([audioData], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);

        // Store reference to current audio element
        this.currentAudio = audio;
        this.pendingAudioElements.push(audio);

        // Add more detailed logging
        audio.onloadstart = () => console.log('Audio load started');
        audio.oncanplay = () => console.log('Audio can play');
        audio.oncanplaythrough = () => console.log('Audio can play through');
        audio.onplay = () => console.log('Audio play event fired');
        audio.onplaying = () => console.log('Audio is playing');

        audio.onended = () => {
          console.log('Audio playback ended naturally');
          URL.revokeObjectURL(url);
          // Remove from pending list
          const index = this.pendingAudioElements.indexOf(audio);
          if (index > -1) {
            this.pendingAudioElements.splice(index, 1);
          }
          if (this.currentAudio === audio) {
            this.currentAudio = null;
          }
          resolve();
        };

        audio.onerror = (error) => {
          console.error('Audio element error:', error);
          console.error('Audio error details:', audio.error);
          URL.revokeObjectURL(url);
          // Remove from pending list
          const index = this.pendingAudioElements.indexOf(audio);
          if (index > -1) {
            this.pendingAudioElements.splice(index, 1);
          }
          if (this.currentAudio === audio) {
            this.currentAudio = null;
          }
          reject(new Error(`Audio error: ${audio.error?.message || 'Unknown error'}`));
        };

        // Set volume to make sure it's audible
        audio.volume = 1.0;

        console.log('Calling audio.play()...');
        audio.play().then(() => {
          console.log('Audio.play() promise resolved - playback started');
        }).catch(error => {
          console.error('Audio.play() promise rejected:', error);
          URL.revokeObjectURL(url);
          // Remove from pending list
          const index = this.pendingAudioElements.indexOf(audio);
          if (index > -1) {
            this.pendingAudioElements.splice(index, 1);
          }
          if (this.currentAudio === audio) {
            this.currentAudio = null;
          }
          reject(error);
        });
      } catch (error) {
        console.error('Error in playWithAudioElement:', error);
        reject(error);
      }
    });
  }

  async tryRawAudioPlayback(audioData) {
    return new Promise((resolve, reject) => {
      try {
        console.log('Attempting to convert raw audio to WAV...');
        
        // Assume common TTS parameters
        const sampleRate = 16000; // Common for TTS
        const numChannels = 1; // Mono
        const bitsPerSample = 16;
        
        // Convert raw audio data to WAV format
        const wavData = this.createWavFromRaw(audioData, sampleRate, numChannels, bitsPerSample);
        
        // Try to play the constructed WAV
        const blob = new Blob([wavData], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);

        audio.onended = () => {
          URL.revokeObjectURL(url);
          console.log('Raw audio converted to WAV and played successfully');
          resolve();
        };

        audio.onerror = (error) => {
          URL.revokeObjectURL(url);
          console.error('Failed to play converted WAV:', error);
          reject(error);
        };

        audio.play().catch(reject);
      } catch (error) {
        console.error('Error in tryRawAudioPlayback:', error);
        reject(error);
      }
    });
  }

  createWavFromRaw(audioData, sampleRate, numChannels, bitsPerSample) {
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = audioData.length;
    const fileSize = 36 + dataSize;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    // RIFF header
    writeString(0, 'RIFF');
    view.setUint32(4, fileSize, true);
    writeString(8, 'WAVE');

    // fmt chunk
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // PCM format
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);

    // data chunk
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    // Copy audio data
    const audioView = new Uint8Array(buffer, 44);
    audioView.set(new Uint8Array(audioData));

    return buffer;
  }

  async tryFallbackPlayback(audioData) {
    return new Promise((resolve, reject) => {
      try {
        // Create blob and audio element for fallback playback
        const blob = new Blob([audioData], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);

        audio.onended = () => {
          URL.revokeObjectURL(url);
          resolve();
        };

        audio.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(error);
        };

        audio.play().catch(reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  reset() {
    // Stop silent audio stream first
    this.stopSilentAudio();

    // Stop and cleanup all pending audio elements
    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      } catch (e) {
        console.warn('Error stopping current audio during reset:', e);
      }
      this.currentAudio = null;
    }

    // Stop all pending audio elements
    this.pendingAudioElements.forEach(audio => {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        console.warn('Error stopping pending audio during reset:', e);
      }
    });
    this.pendingAudioElements = [];

    // Clear audio queue
    this.audioQueue = [];
    this.isPlaying = false;

    // Close the audio context completely
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        this.audioContext.close();
      } catch (error) {
        console.error('Error closing audio context:', error);
      }
    }

    // Reset initialization state
    this.audioContext = null;
    this.isInitialized = false;
  }

  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (error) {
        console.error('Error resuming audio context:', error);
      }
    }
  }

  // Start continuous silent audio to keep AudioContext active
  startSilentAudio() {
    if (!this.audioContext || this.keepAudioAlive) return;
    
    try {
      // Create a silent audio buffer
      const bufferSize = this.audioContext.sampleRate * 1; // 1 second of silence
      const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
      
      // Create buffer source and gain
      this.silenceSource = this.audioContext.createBufferSource();
      this.silenceGain = this.audioContext.createGain();
      
      // Set up the silent buffer
      this.silenceSource.buffer = buffer;
      this.silenceSource.loop = true; // Loop the silence continuously
      this.silenceGain.gain.setValueAtTime(0, this.audioContext.currentTime); // Completely silent
      
      // Connect: buffer source -> gain -> destination
      this.silenceSource.connect(this.silenceGain);
      this.silenceGain.connect(this.audioContext.destination);
      
      // Start the silent audio
      this.silenceSource.start();
      this.keepAudioAlive = true;
      
      console.log('Silent audio buffer started to keep AudioContext active');
      
      // Handle when the source ends
      this.silenceSource.onended = () => {
        this.keepAudioAlive = false;
        this.silenceSource = null;
        this.silenceGain = null;
        console.log('Silent audio stream ended');
      };
      
    } catch (error) {
      console.error('Failed to start silent audio:', error);
      this.keepAudioAlive = false;
    }
  }
  
  // Stop the silent audio stream
  stopSilentAudio() {
    try {
      if (this.silenceSource && this.keepAudioAlive) {
        this.silenceSource.stop();
        // Note: disconnect and cleanup will be handled by the onended event
      }
      
      // Reset state immediately
      this.keepAudioAlive = false;
      console.log('Silent audio stream stop requested');
    } catch (error) {
      console.error('Error stopping silent audio:', error);
      // Force cleanup on error
      this.keepAudioAlive = false;
      this.silenceSource = null;
      this.silenceGain = null;
    }
  }
}

const audioServiceInstance = new AudioService();
export default audioServiceInstance;